<script setup lang="ts">
/**
 * Reusable "Was this helpful?" feedback widget.
 *
 * Reuses the feedback store, so submissions only persist for logged-in users
 * (Firestore requires auth on the `feedback` collection). Logged-out visitors
 * still see the widget but their click is a no-op — same behavior as the help
 * articles this was extracted from.
 *
 * Flow:
 *   ask → Yes → "what did you like?" (praise), recorded on Submit or Skip
 *       → No  → "how can we improve?" (improve), recorded on Submit only
 *   …both paths end at `done`.
 */
const props = withDefaults(
  defineProps<{
    /** Title of the page being rated — included in the feedback message. */
    title: string
    /** Short noun used as the feedback message prefix, e.g. "Article", "Blog post". */
    context?: string
    /** Heading shown above the Yes/No buttons. */
    question?: string
  }>(),
  {
    context: 'Page',
    question: 'Was this helpful?',
  }
)

const feedbackStore = useFeedbackStore()
const toast = useToast()

type Stage = 'ask' | 'improve' | 'praise' | 'done'
const stage = ref<Stage>('ask')
const isSubmittingYes = ref(false)
const isSubmittingNo = ref(false)
const detailMessage = ref('')

// Page identifier stored separately from the free-text message, e.g.
// "Comparison: OnSong" or "Article: Creating sets".
const source = computed(() => `${props.context}: ${props.title}`)

/**
 * Submit through the store with our own confirmation copy. `message` carries
 * only the user's words; sentiment lives in the rating and the page name in
 * `source`. Shows the thank-you unless the submit actually errored.
 */
async function runSubmit(rating: number, message: string) {
  feedbackStore.form.type = 'page'
  feedbackStore.form.rating = rating
  feedbackStore.form.message = message
  feedbackStore.form.source = source.value
  feedbackStore.form.allowDisplay = false

  const result = await feedbackStore.submitFeedback({ silent: true })
  if (result !== false) {
    toast.success('Thank you for your feedback!')
  }
}

// Yes opens the optional "what did you like?" step; the positive rating is
// recorded when they submit or skip it, so the prompt always appears.
function onYes() {
  if (stage.value !== 'ask') return
  stage.value = 'praise'
}

function onNo() {
  if (stage.value !== 'ask') return
  stage.value = 'improve'
}

async function submitImprovement() {
  if (stage.value !== 'improve') return
  isSubmittingNo.value = true
  try {
    await runSubmit(1, detailMessage.value.trim())
    stage.value = 'done'
  } finally {
    isSubmittingNo.value = false
  }
}

// Record the positive rating, optionally enriched with what they liked.
async function submitPraise(includeText: boolean) {
  if (stage.value !== 'praise') return
  isSubmittingYes.value = true
  try {
    const liked = includeText ? detailMessage.value.trim() : ''
    await runSubmit(5, liked)
    stage.value = 'done'
  } finally {
    isSubmittingYes.value = false
  }
}

// "No" path: declining to elaborate records nothing (matches prior behavior).
function skipImprovement() {
  stage.value = 'done'
}
</script>

<template>
  <v-card flat class="mt-12 pa-4">
    <div class="text-center">
      <!-- Initial Yes / No -->
      <template v-if="stage === 'ask'">
        <div class="text-h6 mb-3">{{ question }}</div>
        <v-btn-group>
          <v-btn prepend-icon="$close" variant="text" color="gray" @click="onNo"> No </v-btn>
          <v-btn prepend-icon="$checkCircle" variant="text" color="primary" class="me-2" @click="onYes"> Yes </v-btn>
        </v-btn-group>
      </template>

      <!-- No → how can we improve -->
      <div v-else-if="stage === 'improve'" class="text-left">
        <div class="text-h6 mb-3">How can we improve this {{ context.toLowerCase() }}?</div>
        <v-textarea
          v-model="detailMessage"
          variant="outlined"
          rows="3"
          placeholder="Your feedback will help us make this article more useful"
          class="mb-3"
        ></v-textarea>
        <div class="d-flex justify-end">
          <v-btn variant="text" class="me-2" :disabled="isSubmittingNo" @click="skipImprovement"> Skip </v-btn>
          <v-btn color="primary" :loading="isSubmittingNo" @click="submitImprovement"> Submit </v-btn>
        </div>
      </div>

      <!-- Yes → what did you like (optional) -->
      <div v-else-if="stage === 'praise'" class="text-left">
        <div class="text-h6 mb-1">Glad it helped!</div>
        <div class="text-body-2 text-medium-emphasis mb-3">What did you like? (optional)</div>
        <v-textarea
          v-model="detailMessage"
          variant="outlined"
          rows="3"
          placeholder="Tell us what worked well. It helps us do more of it."
          class="mb-3"
        ></v-textarea>
        <div class="d-flex justify-end">
          <v-btn variant="text" class="me-2" :disabled="isSubmittingYes" @click="submitPraise(false)"> Skip </v-btn>
          <v-btn color="primary" :loading="isSubmittingYes" @click="submitPraise(true)"> Submit </v-btn>
        </div>
      </div>

      <!-- Done -->
      <div v-else class="text-body-1">
        <v-icon icon="$checkCircle" color="success" class="me-2"></v-icon>
        Thank you for your feedback!
      </div>
    </div>
  </v-card>
</template>
